/* eslint-disable require-atomic-updates */
import { bludeltaRequest, BluDeltaResponse } from './network';
import { Invoice, InvoiceWithoutDetections } from './invoice';
import { getToken } from './auth';
import { invoiceCache } from '../../../filterCache';
import { getSuppliers } from './supplierData';
import { getClients } from './clientData';
import { getConfigFromStore } from './customerConfig';
import {
  createEmptyDetection,
  createEmptyDetectedContact,
  createEmptyVatGroup,
  //createEmptyLineItemGroup,
  createEmptyDueDateGroup,
  createEmptyDiscountGroup,
  createEmptyBankGroup,
} from './detectInvoice';
import { getOcrResult } from './ocrResult';
import store from 'store';

export interface InvoiceContainer {
  ContainerId: UUID;
  Name: string;
  Invoices: Invoice[];
  AdditionalProperties: Record<string, string>;
  Created: Time;
  CreatedBy: string;
}

export interface InvoiceContainerWithoutInvoiceDetections
  extends Omit<InvoiceContainer, 'Invoices'> {
  Invoices: InvoiceWithoutDetections[];
}

export interface UploadInvoiceContainerParams {
  /** Name of the User, just for Logging. */
  Name: string | null;
  /** Some optional description if needed, just for logging. */
  Description: string | null;
  /** Name of the selected file with extension */
  FileName: string;
  /** The container that should be uploaded, encoded as base64 string */
  File: Base64;
  /** Used to direct the upload to the correct partner. See: `mandators` API call. */
  ApiIdentifier: string | null;
  AdditionalProperties: {
    ClientId: string | null;
    SenderEmail: string | null;
    ReceiverEmail: string | null;
  };
}

export type ValidatableInvoicesResponse = BluDeltaResponse;

/**
 * The `uploadInvoiceContainer` call is an additional input channel for the BLU DELTA SMB solution.
 * The uploaded container is processed the same way as documents coming from other input channels.
 */
export function uploadInvoiceContainer(
  params: UploadInvoiceContainerParams,
): Promise<ValidatableInvoicesResponse> {
  //const values = { 'X-AccessToken': getToken() };
  const values = { 'X-AccessToken': 'asdf' };
  const { ApiIdentifier, ...rest } = params;
  if (ApiIdentifier) (values as any)['X-ApiIdentifier'] = ApiIdentifier;
  return bludeltaRequest('POST', '/upload/invoiceContainer', rest, values);
}

export interface LockInvoiceContainerParams {
  ContainerId: UUID;
}

export type LockInvoiceContainerResponse = BluDeltaResponse;

/**
 * In order to avoid simultaneous processing of a container by different users, containers
 * need to be locked, when they are displayed in the details view. If a container is already
 * locked by another user, the container must be displayed in readonly mode.
 */
export function lockInvoiceContainer(
  params: LockInvoiceContainerParams,
): Promise<LockInvoiceContainerResponse> {
  return bludeltaRequest('POST', '/lock/invoiceContainer', params);
}

export interface KeepAliveInvoiceContainerParams {
  ContainerId: UUID;
}

export type KeepAliveInvoiceContainerResponse = BluDeltaResponse;

/**
 * Since the lock of a container has a timeout, the GUI has to send cyclic
 * keep alive messages in order to reset the timeout counter of the lock.
 * If the user closed the GUI the keep alive will not be sent anymore and
 * the document lock will be released.
 */
export function keepAliveInvoiceContainer(
  params: KeepAliveInvoiceContainerParams,
): Promise<KeepAliveInvoiceContainerResponse> {
  return bludeltaRequest('POST', '/keepAlive/lock', params);
}

export interface UnlockInvoiceContainerParams {
  ContainerId: UUID;
  AccessToken: string;
}

export type UnlockInvoiceContainerResponse = BluDeltaResponse;

/**
 * After processing the container (or after navigating to the next container without
 * saving) the container needs to be unlocked.
 *
 * On logout token is deleted before unlock container request is made,
 * so it needs to be passed as param while it still exists.
 */
export function unlockInvoiceContainer(
  params: UnlockInvoiceContainerParams,
): Promise<UnlockInvoiceContainerResponse> {
  const { AccessToken, ...rest } = params;

  return bludeltaRequest('POST', '/unlock/invoiceContainer', rest, {
    'X-AccessToken': AccessToken,
  });
}

export interface InvoiceForSplitting {
  InvoiceId: string;
  Pages: { PageNo: number; Deleted: boolean }[];
}

export interface InvoiceForSplittingWithSrc {
  InvoiceId: string;
  Pages: { PageNo: number; Deleted: boolean; Src: string }[];
}

export interface SaveInvoiceContainerParams {
  ContainerId: UUID;
  Name: string;
  Invoices: InvoiceForSplitting[];
  Created: Time;
  CreatedBy: string;
}

export interface SaveInvoiceContainerResponse extends BluDeltaResponse {
  InvoiceContainers: InvoiceContainer[];
}

/**
 * Save the new invoice container structure.
 */
export async function saveInvoiceContainer(params: SaveInvoiceContainerParams) {
  const result = await bludeltaRequest<SaveInvoiceContainerResponse>(
    'POST',
    '/save/invoiceContainer',
    params,
  );
  normalizeInvoiceContainers(result.InvoiceContainers);
  return result;
}

export interface DeleteInvoiceContainerParams {
  ContainerId: UUID;
}

export type DeleteInvoiceContainerResponse = BluDeltaResponse;

/**
 * Delete a single invoice container.
 */
export function deleteInvoiceContainer(params: DeleteInvoiceContainerParams) {
  return bludeltaRequest<DeleteInvoiceContainerResponse>(
    'POST',
    '/delete/invoiceContainer',
    params,
  );
}

export interface GetInvoiceContainerParams {
  id: UUID;
}

export interface GetInvoiceContainerResponse extends BluDeltaResponse {
  InvoiceContainers: InvoiceContainer[];
}

export function addInitialArrayField(field: string, result: GetInvoiceContainerResponse) {
  const emptyArrayField = {
    Height: 0,
    Score: 0,
    Text: '',
    Value: '',
    Width: 0,
    X: 0,
    Y: 0,
  };
  if (!(result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Sender as any)[field]) {
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Sender as any)[field] = [];
  }
  if (
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Sender as any)[field].length ===
    0
  )
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Sender as any)[field].push({
      ...emptyArrayField,
      Name: field,
    });

  if (!(result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Receiver as any)[field]) {
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Receiver as any)[field] = [];
  }
  if (
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Receiver as any)[field]
      .length === 0
  )
    (result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Receiver as any)[field].push({
      ...emptyArrayField,
      Name: field,
    });
}

const findAndPopulateNullField = (
  field: string,
  response: GetInvoiceContainerResponse,
  type: number,
) => {
  if (
    response.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.InvoiceDetailTypePredictions.findIndex(
      (element) => element.TypeName === field,
    ) === -1
  ) {
    response.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.InvoiceDetailTypePredictions.push(
      {
        Height: 0,
        Score: 0,
        Type: type,
        TypeName: field,
        Value: '',
        Width: 0,
        X: 0,
        Y: 0,
      },
    );
  }
};
/**
 * Gets a single invoice container.
 */
export async function getInvoiceContainer(params: GetInvoiceContainerParams) {
  if (invoiceCache.get(params.id)) {
    return invoiceCache.get(params.id);
  }
  const result = await bludeltaRequest<GetInvoiceContainerResponse>(
    'GET',
    `/invoiceContainer?containerId=${params.id}`,
  );
  store.set('invoiceContainerResponse', result);
  const response = await getOcrResult({
    ContainerId: result.InvoiceContainers[0].ContainerId,
    InvoiceId: result.InvoiceContainers[0].Invoices[0].InvoiceId,
  });
  result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.OcrResult = response.OcrResult;

  normalizeInvoiceContainers(result.InvoiceContainers);

  const customerConfig = getConfigFromStore();

  customerConfig.InvoiceDetails.forEach((detail) => {
    if (
      !detail.Name.includes('.') &&
      detail.Name !== 'Receiver' &&
      detail.Name !== 'VatGroup' &&
      detail.Name !== 'DiscountGroup' &&
      detail.Name !== 'DueDateGroup' &&
      detail.Name !== 'BankGroup'
    ) {
      findAndPopulateNullField(detail.Name, result, detail.InvoiceDetailTypeId);
    }
  });

  result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.InvoiceDetailTypePredictions = result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.InvoiceDetailTypePredictions.filter(
    (el) => el,
  );
  const detectInvoiceResponse = result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse;
  customerConfig.InvoiceDetails.forEach((invoiceDetail) => {
    if (
      !detectInvoiceResponse.PredictionGroups ||
      (detectInvoiceResponse.PredictionGroups as any).length == 0
    ) {
      detectInvoiceResponse.PredictionGroups = [];
      if (customerConfig.InvoiceDetails.findIndex((value) => value.Name === 'VatGroup') !== -1)
        detectInvoiceResponse.PredictionGroups.push(createEmptyVatGroup());
      if (customerConfig.InvoiceDetails.findIndex((value) => value.Name === 'DueDateGroup') !== -1)
        detectInvoiceResponse.PredictionGroups.push(createEmptyDueDateGroup());
      if (customerConfig.InvoiceDetails.findIndex((value) => value.Name === 'DiscountGroup') !== -1)
        detectInvoiceResponse.PredictionGroups.push(createEmptyDiscountGroup());
      if (customerConfig.InvoiceDetails.findIndex((value) => value.Name === 'BankGroup') !== -1)
        detectInvoiceResponse.PredictionGroups.push(createEmptyBankGroup());
    }
    //Iterate all invoice details from customer config and check whether they exist to catch missing custom invoice details
    if (
      invoiceDetail.PropertyPath === 'InvoiceDetailTypePredictions' &&
      !detectInvoiceResponse.InvoiceDetailTypePredictions.find(
        (i) => i.TypeName === invoiceDetail.Name,
      )
    ) {
      const emptyInvoiceDetail = createEmptyDetection(
        invoiceDetail.InvoiceDetailTypeId,
        invoiceDetail.Name === 'UId' ? 'VatId' : invoiceDetail.Name,
      );
      detectInvoiceResponse.InvoiceDetailTypePredictions.push(emptyInvoiceDetail);
    }

    //Check Sender, Receiver and Vatgroup existence manually
    else if (invoiceDetail.Name === 'Sender' && !detectInvoiceResponse.Sender) {
      detectInvoiceResponse.Sender = createEmptyDetectedContact();
    } else if (invoiceDetail.Name === 'Receiver' && !detectInvoiceResponse.Receiver) {
      detectInvoiceResponse.Receiver = createEmptyDetectedContact();
    }
  });

  moveAdditionalProperties(result.InvoiceContainers);
  addInitialArrayField('Email', result);
  addInitialArrayField('WebsiteUrl', result);

  let emptySupplier = {
    SupplierId: '',
    Name: '',
    Uids: ['', ''],
    Ibans: ['', ''],
    City: '',
    Country: '',
    Street: '',
    ZipCode: '',
  };
  const emptySupplierResult = {
    SupplierId: '',
    Score: 0,
  };
  const emptyClient = {
    Name: '',
    Id: '',
  };
  let cli = emptyClient;
  if (
    result.InvoiceContainers[0].Invoices.length > 0 &&
    result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties &&
    result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties["ClientId"]
  ) {
    const clients = await getClients({
      ClientId:
        result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties["ClientId"],
    });
    cli = clients.Clients[0];
  }
  if (!cli) {
    if (
      result.InvoiceContainers[0].Invoices.length > 0 &&
      result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties &&
      result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties["ClientId"]
    ) {
      result.InvoiceContainers[0].Invoices[0].BadClientId =
        result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.AdditionalProperties["ClientId"];
    }
    cli = emptyClient;
  }
  result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Client = cli;
  if (!result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Client!.Id)
    emptySupplier = {
      SupplierId: ' ',
      Name: ' ',
      Uids: [' ', ' '],
      Ibans: [' ', ' '],
      City: ' ',
      Country: ' ',
      Street: ' ',
      ZipCode: ' ',
    };
  let supp = emptySupplier;
  if (
    result.InvoiceContainers[0].Invoices.length > 0 &&
    result.InvoiceContainers[0].Invoices[0].SupplierResult.SupplierId
  ) {
    const suppliers = await getSuppliers({
      columnName: 'SupplierId',
      searchText: result.InvoiceContainers[0].Invoices[0].SupplierResult.SupplierId,
      ClientId: result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.Client.Id,
    });
    supp = suppliers.Suppliers[0];
  }
  if (!supp) {
    if (
      result.InvoiceContainers[0].Invoices.length > 0 &&
      result.InvoiceContainers[0].Invoices[0].SupplierResult.SupplierId
    ) {
      result.InvoiceContainers[0].Invoices[0].BadSupplierId =
        result.InvoiceContainers[0].Invoices[0].SupplierResult.SupplierId;
      result.InvoiceContainers[0].Invoices[0].SupplierResult = emptySupplierResult;
    }
    supp = emptySupplier;
  }
  result.InvoiceContainers.forEach((container) => {
    container.Invoices.forEach((invoice) => {
      invoice.DetectInvoiceResponse.Supplier = supp;
    });
  });
  result.InvoiceContainers[0].Invoices[0].DetectInvoiceResponse.InvoiceDetailTypePredictions.forEach(
    (item) => {
      if (
        item.TypeName === 'DocumentType' &&
        item.Value !== 'Invoice' &&
        item.Value !== 'CreditMemo'
      ) {
        item.Value = 'Invoice';
      }
    },
  );
  normalizeFieldArrays(result.InvoiceContainers);
  invoiceCache.set(params.id, result);
  return result;
}

export function normalizeFieldArrays(invoiceContainers: InvoiceContainer[]) {
  invoiceContainers.forEach((container) => {
    container.Invoices.forEach((invoice) => {
      normalizeField(invoice.DetectInvoiceResponse.Supplier!.Ibans);
      normalizeField(invoice.DetectInvoiceResponse.Supplier!.Uids);
    });
  });
}

export function normalizeField(values: string[]) {
  while (values.length < 2) values.push('');
  while (values.length > 2) values.pop();
}

// Normalizes a set of invoice containers. BEWARE: the function mutates the object.
export function normalizeInvoiceContainers(invoiceContainers: InvoiceContainer[]) {
  invoiceContainers.forEach((invoiceContainer) => {
    if (invoiceContainer.Invoices && invoiceContainer.Invoices.length)
      normalizeInvoiceContainer(invoiceContainer);
  });
}

export function moveAdditionalProperties(invoiceContainers: InvoiceContainer[]) {
  invoiceContainers.forEach((element) => {
    element.Invoices.forEach((invoice) => {
      invoice.DetectInvoiceResponse['AdditionalProperties'] = element.AdditionalProperties;
    });
  });
}

// Normalizes the invoice container. BEWARE: the function mutates the object.
export function normalizeInvoiceContainer(invoiceContainer: InvoiceContainer) {
  invoiceContainer.Invoices.forEach((invoice) => {
    invoice.Pages.sort(
      ({ PageNo: leftPageNo }, { PageNo: rightPageNo }) => leftPageNo - rightPageNo,
    );
    if (!invoice.DetectInvoiceResponse.OcrResult) {
      // make sure the ocr is really `undefined` because it might sometimes be an empty string
      invoice.DetectInvoiceResponse.OcrResult = undefined;
      return;
    }

    // the ocr result is often a stringified JSON
    if (typeof invoice.DetectInvoiceResponse.OcrResult === 'string') {
      invoice.DetectInvoiceResponse.OcrResult = JSON.parse(invoice.DetectInvoiceResponse.OcrResult);
    }
  });
}
